"use client";

import { ContentLink } from "@components/cms/link/ContentLink";
import {
    categoryTitleMobileStyles,
    mobileButtonBackStyles,
    mobileLinkStyles,
    navItemMobileStyles,
} from "@components/layouts/navigation/styles";
import type { NavigationLink } from "@lib/contentful/mappers/mapNavigation";
import {
    SECOND_LEVEL_NAVIGATION_BUTTON,
    THIRD_LEVEL_NAVIGATION_LINK,
} from "@e2e/helpers/navigation/constants";
import { Text } from "@ecco/ui/text";
import { Button } from "@ecco/ui/button";
import { Flex } from "@ecco/ui/flex";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import space from "@ecco/ui/tokens/space";
import { useDisclosure } from "@ecco/ui/use-disclosure";
import { motion } from "framer-motion";
import { usePathname } from "next/navigation";
import type { FC } from "react";
import { useEffect, useState } from "react";

interface IMobileSubNavItemProps {
    title?: string;
    links: NavigationLink[];
}

export const MobileSubNavItem: FC<IMobileSubNavItemProps> = ({ title, links }) => {
    const { getButtonProps, getDisclosureProps, isOpen, onClose } = useDisclosure();
    const asPath = usePathname();
    const [hidden, setHidden] = useState(!isOpen);

    /* We need the timeout to close the current submenu, otherwise the load will block the animation */
    useEffect(() => {
        const interval = setTimeout(() => {
            onClose();
        }, 1000);
        return () => clearTimeout(interval);
    }, [asPath, onClose]);

    return (
        <>
            <Flex
                data-testid={SECOND_LEVEL_NAVIGATION_BUTTON}
                as={Button}
                variant="solid"
                colorScheme="white"
                _hover={{ bg: "transparent" }}
                _active={{ bg: "transparent" }}
                _focus={{ bg: "transparent" }}
                sx={navItemMobileStyles}
                {...getButtonProps()}
            >
                <Text sx={categoryTitleMobileStyles}>{title}</Text>
                <ECCOIcon
                    color="black"
                    aria-label="Right Arrow"
                    name="chevron"
                    transform="rotate(-90deg)"
                />
            </Flex>
            <motion.div
                // Version mismatch causing disclosure props for events to not match with framer-motion
                {...(getDisclosureProps() as any)}
                hidden={hidden}
                initial={false}
                onAnimationStart={() => setHidden(false)}
                onAnimationComplete={() => setHidden(!isOpen)}
                animate={{ width: isOpen ? "100%" : 0 }}
                transition={{ ease: "easeOut", duration: 0.15 }}
                style={{
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    position: "absolute",
                    right: "0",
                    height: "calc(100vh - 35px)",
                    top: "0",
                    zIndex: 3,
                    marginTop: 0,
                    display: "flex",
                    flexDirection: "column",
                    padding: "0 1px",
                }}
            >
                {/* Mobile ipad air view third menu adjust mb to 14+ or so */}
                <Flex bg="white" mb={"4px"}>
                    <Flex
                        as={Button}
                        colorScheme="white"
                        variant="solid"
                        _hover={{ bg: "transparent" }}
                        _active={{ bg: "transparent" }}
                        _focus={{ bg: "transparent" }}
                        sx={mobileButtonBackStyles}
                        {...getButtonProps()}
                    >
                        <ECCOIcon
                            color="black"
                            aria-label="Left Arrow"
                            name="chevron"
                            transform="rotate(90deg)"
                        />
                        <Text sx={{ ...categoryTitleMobileStyles, color: "black" }}>{title}</Text>
                    </Flex>
                </Flex>
                <Flex flexDirection={"column"} bg="white" mt={space["1"]} h={"100%"}>
                    {links.length > 0 &&
                        links.map((link) => (
                            <ContentLink
                                data-testid={THIRD_LEVEL_NAVIGATION_LINK}
                                link={link.contentLink}
                                key={link.id}
                                sx={mobileLinkStyles}
                            />
                        ))}
                </Flex>
            </motion.div>
        </>
    );
};
